import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { dynamicFormFields } from "./edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";

const filterN1StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterN1StaticDatumsFields = filterN1StaticDatumsSchema.keyof().enum;

export type FilterN1StaticDatumsSchema = z.infer<
  typeof filterN1StaticDatumsSchema
>;

const assetDepFields: readonly DepField<FilterN1StaticDatumsSchema>[] = [
  {
    name: filterN1StaticDatumsFields.name_contains,
    key: dynamicFormFields.id,
    extract: dynamicFormFields.id,
    bucket: "equals",
  },
  {
    name: filterN1StaticDatumsFields.isin_contains,
    key: dynamicFormFields.isin,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterN1StaticDatumsFields.code_contains,
    key: dynamicFormFields.code,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterN1StaticDatumsFields.ticker_contains,
    key: dynamicFormFields.ticker,
    extract: "text",
    bucket: "contains",
  },
];

export function FilterN1StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterN1StaticDatumsFields.name_contains,
      label: t("n1_static_data.searchbox.name_contains", { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      column_name: dynamicFormFields.name,
      filters: makeFiltersBuilder<FilterN1StaticDatumsSchema>(
        assetDepFields,
        filterN1StaticDatumsFields.name_contains,
      ),
    },
    {
      name: filterN1StaticDatumsFields.isin_contains,
      label: t("n1_static_data.searchbox.isin_contains", { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      column_name: dynamicFormFields.isin,
      filters: makeFiltersBuilder<FilterN1StaticDatumsSchema>(
        assetDepFields,
        filterN1StaticDatumsFields.isin_contains,
      ),
    },
    {
      name: filterN1StaticDatumsFields.code_contains,
      label: t("n1_static_data.searchbox.code_contains", { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      column_name: dynamicFormFields.code,
      filters: makeFiltersBuilder<FilterN1StaticDatumsSchema>(
        assetDepFields,
        filterN1StaticDatumsFields.code_contains,
      ),
    },
    {
      name: filterN1StaticDatumsFields.ticker_contains,
      label: t("n1_static_data.searchbox.ticker_contains", { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      column_name: dynamicFormFields.ticker,
      filters: makeFiltersBuilder(
        assetDepFields,
        filterN1StaticDatumsFields.ticker_contains,
      ),
    },
    {
      name: filterN1StaticDatumsFields.currency_contains,
      label: t("n1_static_data.searchbox.currency_contains", { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterN1StaticDatumsFields.asset_class_1_equals,
      assetSubclassFieldName: filterN1StaticDatumsFields.asset_class_2_equals,
      assetClassLabel: t("n1_static_data.searchbox.asset_class_1_equals", { defaultValue: "Asset class" }),
      assetSubclassLabel: t("n1_static_data.searchbox.asset_class_2_equals", { defaultValue: "Sub asset class" }),
      client: Clients.N1,
      staticDataTableName: Tables.N1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetClassTranslateKey: dynamicFormFields.asset_class_1,
      assetSubclassTranslateKey: dynamicFormFields.asset_class_2,
      assetSubclassFieldType: "select",
    }),
  ];

  return (
    <FilterTable
      schema={filterN1StaticDatumsSchema}
      client={`${Clients.N1}`}
      tableName={`${Tables.N1_STATIC_DATA}`}
      fields={fields}
    />
  );
}
